import { Fragment, useRef, useState, useContext, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../authConfig";
import { Dialog, Transition } from "@headlessui/react";
import { AppStateContext } from "../../state/AppProvider";
import { sendSupportEmail } from "../../api";
import { SendEmailRequest, ChatMessage } from "../../api/models";
import "./EmailDialog.css";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    messages: ChatMessage[];
}

export const EmailDialog = ({ open, setOpen, messages }: Props) => {
    const { instance: client } = useMsal();
    const appStateContext = useContext(AppStateContext);

    const cancelButtonRef = useRef(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [question, setQuestion] = useState("");
    const [invalid, setInvalid] = useState(false);
    const [sent, setSent] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const currentAssistant = appStateContext?.state.assistant?.name ?? "";
    const assistantEmail = appStateContext?.state.assistant?.contact?.email ?? "";

    useEffect(() => {
        if (!sent) setDisabled(false);
    }, [sent]);

    const sendQuestion = async () => {
        if (!question.trim()) return setInvalid(true);

        if (!assistantEmail) {
            setErrorMsg("Support contact email is not configured. Please contact IT and try again later.");
            return;
        }

        setDisabled(true);
        setErrorMsg(null);
        try {
            const token = client ? await getToken(client) : undefined;
            const emailRequest: SendEmailRequest = {
                subject: "Support Request",
                question,
                messages,
                recipient: assistantEmail,
                assistant_name: currentAssistant
            };
            const res = await sendSupportEmail(emailRequest, token?.accessToken);
            if (res && "message" in res) {
                setSent(true);
                setInvalid(false);
                setQuestion("");
                setTimeout(() => onClose(), 2000);
            } else if (res && "error" in res) {
                setErrorMsg(res.error);
                setDisabled(false);
            } else {
                setErrorMsg("An unexpected error occurred while sending your email.");
                setDisabled(false);
            }
        } catch (err) {
            console.error("Error sending email:", err);
            setErrorMsg("An unexpected error occurred while sending your email.");
            setDisabled(false);
        }
    };

    const onClose = () => {
        setOpen(false);
        setInvalid(false);
        setQuestion("");
        setSent(false);
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog className="relative z-[100]" initialFocus={cancelButtonRef} onClose={onClose}>
                {/* backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                {sent && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center translate-y-2 pointer-events-none z-20">
                                        <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                            <circle className="checkmark__circle" cx="26" cy="26" r="25" />
                                            <path className="checkmark__check" d="M14 27l7 7 16-16" />
                                        </svg>
                                        <h2 className="mt-1 text-lg font-semibold text-eq-green">Message sent!</h2>
                                    </div>
                                )}
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start gap-4">
                                        {/* assistant icon */}
                                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-eq-light-grey">
                                            <span className="material-symbols-outlined text-[1.5rem] text-eq-green">
                                                {appStateContext?.state.assistant?.contact.icon}
                                            </span>
                                        </div>
                                        {/* form area */}
                                        <div className="w-full text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Email {appStateContext?.state.assistant?.contact.name}
                                            </Dialog.Title>
                                            <p className="mt-2 text-sm text-gray-500">
                                                If {appStateContext?.state.assistant?.name} was unable to answer your question, or you have a question that
                                                requires a human touch, please reach out to us by completing the below. Your message, and a transcript of your
                                                conversation, will be forwarded to the relevant support team.
                                            </p>
                                            {errorMsg && <div className="mt-2 text-sm text-red-600">Email not sent - {errorMsg}</div>}
                                            <textarea
                                                ref={textAreaRef}
                                                className={`text-sm mt-3 h-[200px] w-full resize-none border p-2 bg-transparent placeholder-eq-grey focus-visible:outline-none focus:ring-transparent focus:ring-1 ${invalid ? "border-eq-grey" : "border-eq-light-grey"} ${sent ? "opacity-0" : "opacity-100"}`}
                                                placeholder={invalid ? "A message is required..." : "Enter your message here..."}
                                                value={question}
                                                onChange={e => {
                                                    setQuestion(e.target.value);
                                                    setInvalid(false);
                                                }}
                                                disabled={sent}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row gap-4 px-4 pb-6 sm:flex-row-reverse sm:px-6">
                                    <button
                                        className="eq-primary-button m-0 w-[115px] disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={sendQuestion}
                                        disabled={disabled || sent}
                                    >
                                        Send
                                    </button>
                                    <button className="eq-secondary-button m-0 w-[115px]" onClick={onClose}>
                                        Dismiss
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
